import React, { useMemo, useState } from 'react'
import { CCard, CCardBody, CCardHeader, CSmartTable } from '@coreui/react-pro'
import { CChart } from '@coreui/react-chartjs'
import { useChartTheme, baseChartOptions, axis } from './chartTheme'
import {
  ChartSkeleton,
  EmptyState,
  ErrorState,
  computeDelta,
  formatDecimal,
  formatNumber,
} from './common'
import ScreenFlowOffcanvas from './ScreenFlowOffcanvas'

const columns = [
  { key: 'page_name', label: 'Screen', _style: { minWidth: '220px' } },
  { key: 'views', label: 'Views', _style: { width: '14%' } },
  { key: 'unique_users', label: 'People', _style: { width: '14%' } },
  { key: 'unique_sessions', label: 'Sessions', _style: { width: '14%' } },
  { key: 'share_pct', label: 'Share of views', _style: { width: '14%' } },
  { key: 'trend', label: 'vs previous', filter: false, sorter: false, _style: { width: '14%' } },
]

/**
 * Ranking chart — horizontal, sorted descending. Horizontal because screen
 * names like MySquadPlayerDetails get clipped on a vertical axis.
 */
const TopScreensChart = ({ rows, theme }) => {
  const top = useMemo(() => (rows || []).slice(0, 10), [rows])

  const data = {
    labels: top.map((row) => row.page_name),
    datasets: [
      {
        label: 'Screen views',
        data: top.map((row) => Number(row.views) || 0),
        backgroundColor: theme.series[0],
        borderRadius: 4,
        borderSkipped: 'start',
        barThickness: 20,
      },
    ],
  }

  return (
    <div style={{ height: Math.max(220, top.length * 34 + 40) }}>
      <CChart
        type="bar"
        data={data}
        options={baseChartOptions(theme, {
          indexAxis: 'y',
          // A single series names itself in the card title — a one-swatch legend
          // would just restate it.
          plugins: { legend: { display: false } },
          scales: {
            x: axis(theme, { beginAtZero: true, ticks: { color: theme.textMuted, precision: 0 } }),
            y: axis(theme, {
              grid: { display: false },
              ticks: { color: theme.text, autoSkip: false },
            }),
          },
        })}
      />
    </div>
  )
}

const ScreensTab = ({ screens, filters, loading, error, onRetry }) => {
  const theme = useChartTheme()
  const [selected, setSelected] = useState(null)

  const rows = useMemo(() => screens?.rows || [], [screens])
  const hasData = rows.length > 0

  const tableItems = useMemo(
    () =>
      rows.map((row) => ({
        ...row,
        views: Number(row.views) || 0,
        unique_users: Number(row.unique_users) || 0,
        unique_sessions: Number(row.unique_sessions) || 0,
        share_pct: Number(row.share_pct) || 0,
      })),
    [rows],
  )

  return (
    <>
      <CCard className="mb-4">
        <CCardHeader>
          <strong>Most visited screens</strong>
          <div className="small text-body-secondary">
            Top 10 by screen views. Select any row in the table below to see how people reach it.
          </div>
        </CCardHeader>
        <CCardBody>
          {loading && <ChartSkeleton height={320} />}
          {!loading && error && <ErrorState message={error} onRetry={onRetry} height={320} />}
          {!loading && !error && !hasData && <EmptyState height={320} />}
          {!loading && !error && hasData && <TopScreensChart rows={rows} theme={theme} />}
        </CCardBody>
      </CCard>

      <CCard className="mb-4">
        <CCardHeader>
          <strong>All screens</strong>
          <div className="small text-body-secondary">
            {screens?.total
              ? `${formatNumber(screens.total)} screens visited in this period. `
              : ''}
            Search, sort, or select a screen for its journey.
          </div>
        </CCardHeader>
        <CCardBody>
          {loading && <ChartSkeleton height={280} />}
          {!loading && error && <ErrorState message={error} onRetry={onRetry} height={280} />}
          {!loading && !error && !hasData && <EmptyState height={280} />}
          {!loading && !error && hasData && (
            <div className="av2-scroll-x">
              <CSmartTable
                items={tableItems}
                columns={columns}
                columnFilter
                columnSorter
                tableFilter
                tableFilterLabel="Find a screen:"
                tableFilterPlaceholder="e.g. MatchScorecard"
                itemsPerPage={15}
                itemsPerPageSelect
                pagination
                clickableRows
                noItemsLabel="No screens match that search"
                onRowClick={(item) => setSelected(item.page_name)}
                sorterValue={{ column: 'views', state: 'desc' }}
                tableProps={{ hover: true, responsive: true, align: 'middle' }}
                scopedColumns={{
                  page_name: (item) => (
                    <td>
                      <span className="av2-screen-name">{item.page_name}</span>
                    </td>
                  ),
                  views: (item) => <td>{formatNumber(item.views)}</td>,
                  unique_users: (item) => <td>{formatNumber(item.unique_users)}</td>,
                  unique_sessions: (item) => <td>{formatNumber(item.unique_sessions)}</td>,
                  share_pct: (item) => (
                    <td className="text-body-secondary">{formatDecimal(item.share_pct, 1)}%</td>
                  ),
                  trend: (item) => {
                    const delta = computeDelta(item.views, item.previous_views)
                    return (
                      <td>
                        <span className={`text-${delta.color}`}>{delta.label}</span>
                      </td>
                    )
                  },
                }}
              />
            </div>
          )}
        </CCardBody>
      </CCard>

      <ScreenFlowOffcanvas
        visible={Boolean(selected)}
        pageName={selected}
        filters={filters}
        onClose={() => setSelected(null)}
      />
    </>
  )
}

export default ScreensTab
